import { useMemo } from 'react';
import { Lead, FunnelStage } from '../../../../types';
import ActionMenu, { createDeleteAction, createDuplicateAction } from '../../../../components/shared/ActionMenu';
import { Button } from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';

interface LeadHeaderProps {
    lead: Lead | null;
    localLead: Lead | null;
    funnelStages: FunnelStage[];
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    onDelete: (lead: Lead) => void;
    onMarkAsWon: () => void;
    onMarkAsLost: () => void;
    onReopen?: () => void;
}

export const LeadHeader: React.FC<LeadHeaderProps> = ({
    lead,
    localLead,
    funnelStages,
    onInputChange,
    onDelete,
    onMarkAsWon,
    onMarkAsLost,
    onReopen
}) => {
    const stageOptions = useMemo(
        () => funnelStages.map((s) => ({ value: s.id, label: s.name })),
        [funnelStages],
    );

    if (!localLead || !lead) return null;

    const isFinalStatus = localLead.status === 'won' || localLead.status === 'lost';

    const stageValue =
        localLead.status === 'won'
            ? 'won'
            : localLead.status === 'lost'
                ? 'lost'
                : localLead.stage;

    return (
        <div className="px-s4 pb-s6 pt-s4 md:px-s6">
            <div className="mb-s4 flex items-center justify-between gap-s3">
                <Input
                    type="text"
                    name="name"
                    value={localLead.name}
                    onChange={onInputChange}
                    placeholder="Nome do lead"
                    aria-label="Nome do lead"
                    variant="filled"
                    inputSize="lg"
                    containerClassName="min-w-0 flex-1"
                    className="!h-auto !min-h-0 !border-none !bg-transparent p-0 text-xl font-bold tracking-tight text-default !shadow-none focus-visible:!ring-0 md:text-2xl"
                />
            </div>

            <div className="flex flex-wrap items-center gap-s2">
                <Select
                    name="stage"
                    value={stageValue}
                    onChange={onInputChange}
                    options={stageOptions}
                    selectSize="sm"
                    containerClassName="w-auto"
                    className="cursor-pointer rounded-full border-none bg-primary-subtle px-s4 py-s2 text-sm font-semibold text-primary-dark transition-all hover:bg-brand-100 focus:ring-2 focus:ring-ring/40 !pr-s8"
                />

                {isFinalStatus ? (
                    <Button
                        onClick={onReopen}
                        variant="outline"
                        className="rounded-full px-s6 font-medium"
                        size="sm"
                    >
                        <span>Reabrir</span>
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={onMarkAsWon}
                            variant="success"
                            className="rounded-full px-s6 shadow-sm"
                            size="sm"
                        >
                            <span>Ganho</span>
                        </Button>
                        <Button
                            onClick={onMarkAsLost}
                            variant="danger"
                            className="rounded-full px-s6 shadow-sm"
                            size="sm"
                        >
                            <span>Perdido</span>
                        </Button>
                    </>
                )}

                <ActionMenu
                    strategy="absolute"
                    actions={[
                        createDuplicateAction(() => {
                            console.log('Duplicar lead');
                        }),
                        createDeleteAction(() => onDelete(lead))
                    ]}
                />
            </div>
        </div>
    );
};
